/**
 * ============================================================================
 * MembershipFormModal - MODERN ADD/EDIT MODAL
 * ============================================================================
 *
 * Clean, modern modal for creating and editing membership packages
 * Features:
 * - Responsive design
 * - Validation
 * - Escape key support
 * - Loading states
 *
 * ============================================================================
 */

import { CURRENCY_SYMBOL } from '../../utils/currency';
import React, { useState, useEffect } from 'react';
import { X, Save, Plus, Minus } from 'lucide-react';
import type { MembershipPackage, DurationType } from '../../types/memberships';
import Modal from '../ui/Modal';

interface MembershipFormModalProps {
  isOpen: boolean;
  package: MembershipPackage | null; // null = add mode, MembershipPackage = edit mode
  onClose: () => void;
  onSubmit: (formData: MembershipFormData) => void;
  isSaving?: boolean;
}

export interface MembershipFormData {
  name: string;
  description: string;
  price: number | '';
  duration: number | '';
  duration_type: DurationType;
  facilities: string[];
  status: 'active' | 'inactive';
}

const initialFormData: MembershipFormData = {
  name: '',
  description: '',
  price: '',
  duration: 3,
  duration_type: 'month',
  facilities: [],
  status: 'active',
};

const DURATION_TYPES: DurationType[] = ['day', 'week', 'month', 'year'];

/**
 * Membership add/edit form modal
 */
export function MembershipFormModal({
  isOpen,
  package: pkg,
  onClose,
  onSubmit,
  isSaving = false,
}: MembershipFormModalProps) {
  const [formData, setFormData] = useState<MembershipFormData>(initialFormData);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [newFacility, setNewFacility] = useState('');

  const isEditMode = !!pkg;

  // Load package data when editing
  useEffect(() => {
    if (pkg) {
      setFormData({
        name: pkg.name,
        description: pkg.description,
        price: pkg.price,
        duration: pkg.duration,
        duration_type: pkg.duration_type,
        facilities: pkg.facilities || [],
        status: pkg.status === 'active' ? 'active' : 'inactive',
      });
    } else {
      setFormData(initialFormData);
    }
    setErrors({});
  }, [pkg, isOpen]);

  const handleChange = (field: keyof MembershipFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const addFacility = () => {
    if (newFacility.trim()) {
      setFormData((prev) => ({
        ...prev,
        facilities: [...prev.facilities, newFacility.trim()],
      }));
      setNewFacility('');
    }
  };

  const removeFacility = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      facilities: prev.facilities.filter((_, i) => i !== index),
    }));
  };

  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Package name is required';
    }
    if (!formData.price || Number(formData.price) <= 0) {
      newErrors.price = 'Price must be greater than 0';
    }
    if (!formData.duration || Number(formData.duration) <= 0) {
      newErrors.duration = 'Duration must be greater than 0';
    }
    if (formData.facilities.length === 0) {
      newErrors.facilities = 'Add at least one facility/privilege';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    onSubmit(formData);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" preventClose={isSaving} animate={false}>
        {/* Header - Fixed */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-white rounded-t-2xl flex-shrink-0">
          <h2 className="text-xl font-bold text-gray-900">
            {isEditMode ? `Edit ${pkg.name}` : 'Create New Membership Package'}
          </h2>
          <button
            onClick={onClose}
            disabled={isSaving}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form - Scrollable */}
        <form id="membership-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Package Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Package Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g., Premium Gym Membership"
              maxLength={100}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value as 'active' | 'inactive')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Price & Duration */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price ({CURRENCY_SYMBOL}) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                step="100"
                value={formData.price}
                onChange={(e) => handleChange('price', e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="25000"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.price ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.price && <p className="text-xs text-red-500 mt-1">{errors.price}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Duration <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="1"
                  value={formData.duration}
                  onChange={(e) => handleChange('duration', e.target.value === '' ? '' : Number(e.target.value))}
                  placeholder="3"
                  className={`w-20 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.duration ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                <select
                  value={formData.duration_type}
                  onChange={(e) => handleChange('duration_type', e.target.value as DurationType)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {DURATION_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              {errors.duration && <p className="text-xs text-red-500 mt-1">{errors.duration}</p>}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Describe what's included in this package..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Facilities/Privileges */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Facilities & Privileges <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newFacility}
                onChange={(e) => setNewFacility(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFacility())}
                placeholder="e.g., Access to all courts, Free parking"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={addFacility}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
            </div>

            {formData.facilities.length > 0 && (
              <div className="space-y-2 mb-2">
                {formData.facilities.map((facility, index) => (
                  <div key={index} className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg">
                    <span className="flex-1 text-sm text-gray-700">• {facility}</span>
                    <button
                      type="button"
                      onClick={() => removeFacility(index)}
                      className="text-red-500 hover:text-red-700 transition-colors"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {errors.facilities && <p className="text-xs text-red-500">{errors.facilities}</p>}
          </div>
        </form>

        {/* Form Actions - Fixed at bottom */}
        <div className="px-6 py-4 border-t border-gray-200 bg-white rounded-b-2xl flex items-center justify-end space-x-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="membership-form"
            disabled={isSaving}
            className="flex items-center px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Saving...' : isEditMode ? 'Save Changes' : 'Create Package'}
          </button>
        </div>
    </Modal>
  );
}
